import { useContext, useEffect, useRef, useState } from 'react'
import useWebSocketLib, { ReadyState } from 'react-use-websocket'

import { AuthSessionContext } from '@context/AuthContext'

interface UseWebSocketOptions {
  share?: boolean
  onOpen?: () => void
  onClose?: () => void
  onError?: (event: WebSocketEventMap['error']) => void
  onMessage?: (event: MessageEvent) => void
  heartbeat?:
    | false
    | {
        message: string
        returnMessage?: string
        timeout: number
        interval: number
      }
}

interface UseWebSocketResult {
  sendMessage: (message: string) => void
  lastMessage: MessageEvent | null
  readyState: ReadyState
  connectionStatus: string
  messageHistory: MessageEvent[]
}

export const useWebSocket = (path: string, options?: UseWebSocketOptions): UseWebSocketResult => {
  const [socketUrl, setSocketUrl] = useState<string | null>(null)
  const [messageHistory, setMessageHistory] = useState<MessageEvent<any>[]>([])
  const [auth] = useContext(AuthSessionContext)
  const didUnmount = useRef(false)

  useEffect(() => {
    setSocketUrl(`/api/${path}/ws`)
    didUnmount.current = false
    return () => {
      didUnmount.current = true
      setSocketUrl(null)
      setMessageHistory([])
    }
  }, [auth?.accessToken, path])

  const { sendMessage, lastMessage, readyState } = useWebSocketLib(socketUrl, {
    share: options?.share ?? false,
    queryParams:
      process.env.NODE_ENV === 'development'
        ? {
            token: auth?.accessToken ?? '',
          }
        : undefined,
    onOpen: () => {
      options?.onOpen?.()
    },
    onClose: () => {
      options?.onClose?.()
    },
    onError: (event: Event) => {
      console.error('WebSocket error:', event)
      options?.onError?.(event)
    },
    heartbeat: false,
    // We will try to reconnect for 2 minutes (120 attempts with 1 second interval)
    shouldReconnect: () => !didUnmount.current,
    reconnectAttempts: 120,
    reconnectInterval: 1000,
    onMessage: (event: MessageEvent<any>) => {
      // reply to server-initiated ping with pong
      if (event.data === 'ping') {
        sendMessage('pong')
        return
      }
      // ignore pong responses
      if (event.data === 'pong') {
        return
      }
      setMessageHistory((prev) => prev.concat(event))
      options?.onMessage?.(event)
    },
  })

  useEffect(() => {}, [readyState])

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState]

  return {
    sendMessage,
    lastMessage,
    readyState,
    connectionStatus,
    messageHistory,
  }
}
